import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { PaymentSources } from '../enums/enums'
import { ILicense } from '../Interface/license'
import { ICartTrack, ITrack } from '../Interface/track'
import { SubscribingObject } from '../player/common/SubscribingObject'
import { CartApiService, CheckResponseType } from './cart-api.service'
import { CartStorageService } from './cart-storage.service'
import { CoreEnvironmentService } from './core-environment.service'

export interface ICartGroup {
  beatmakerId: number
  beatmakerName: string
  beats: ICartTrack[]
  fullPrice: number
  hasDiscount: false
  isOK: boolean
  priceWithDiscount: number
  coupon: string
  hasRequiredPassportLicense: boolean
  cashbackAmount: number
}

export type LocalStorageShoppingCart = {
  beatId: number
  licenseId: string
}

type ResponseLicenseType = {
  beatId: number
  licenses: Array<ILicense>
}

@Injectable()
export class CartService extends SubscribingObject<any> {
  groups: any = { getGroups: () => [] }
  private readonly storageKeyForShopingCart = 'shoping-cart'
  keyId: number = null

  constructor(
    public http: HttpClient,
    public environment: CoreEnvironmentService,
    private cartStorageService: CartStorageService,
    private cartApiService: CartApiService
  ) {
    super()
    this.addSubscriber(this.storageKeyForShopingCart, () => {
      this.getFullBeats()
    })

    if (!!this.keyId) {
      this.storageKeyForShopingCart += '-' + this.keyId
    }

    // Синхронизация корзины (localstorage) между вкладками
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === this.storageKeyForShopingCart) {
        this.getFullBeats()
      }
    })
  }

  // check if payments was confirmed and we should clear any group of beats
  async checkPayments() {
    const payments = this.cartStorageService.getPayments()

    for (const p of payments) {
      const data = await this.cartApiService.isConfirmed(p.paymentId, p.altPaymentId)
      if (data.confirmed || p.checkingCount > 4) {
        const filteredPayments = payments.filter(pb => pb.beatmakerId !== p.beatmakerId)
        await this.cartStorageService.savePayments(filteredPayments)
        if (data.confirmed) {
          // this group was purchased ao we should clear it
          this._clearGroup(p.beatmakerId)
        }
      } else {
        p.checkingCount++
        await this.cartStorageService.savePayments(payments)
      }
    }
  }

  wantAddBeat(id: number, licenseId?: string) {
    this.triggerEvent('want-add-beat', { beatId: id, licenseId })
  }

  // remove all beats for group
  _clearGroup(beatmakerId: number) {
    if (!!this.groups[beatmakerId]) {
      this.groups[beatmakerId].beats.forEach(b => {
        this.removeBeat(b.id)
      })
    }
  }

  addBeat(beatId: number, licenseId: string) {
    this.cartStorageService.addBeat(beatId, licenseId)

    this.triggerGroupUpdated()

    // let beatmakerIdWithBeatIndex = this.groups.getBeatmakerIdAndBeatIndex(beatId);
    // this._removePaymentAndSave(beatmakerIdWithBeatIndex.beamakerId);
    return this.getFullBeats()
  }

  // достать ВСЕ биты из корзины
  getBeats(): LocalStorageShoppingCart[] {
    return this.cartStorageService.getBeats()
  }

  // получи полные данные с сервера, потому что ты ничего не знаешь, кроме id битов и id лицензий.
  // а ему нужны цены, название лицензий, битов, автор бита и так далее...
  getFullBeats() {
    const service = this
    const beatsIds = this._getBeatsIds()
    this._initGroup()

    if (beatsIds.length === 0) {
      return new Promise((res, rej) => {
        this.triggerGroupUpdated()
        res({ ok: true })
      })
    }

    const beatsDeferred = this.cartApiService.getBeats(beatsIds)
    const licensesDeferred = this.getLicensesForBeats()

    return Promise.all([beatsDeferred, licensesDeferred])
      .then(commonResult => {
        const beats: Array<ITrack> = commonResult[0]
        const licenses: Array<ResponseLicenseType> = commonResult[1]

        // reset all beats
        this.groups.getGroups().forEach(g => (g.beats = []))

        beats.forEach(b => {
          if (!this.groups[b.beatmakerId]) {
            this.groups[b.beatmakerId] = {
              beatmakerId: b.beatmakerId,
              beatmakerName: b.beatmakerName,
              beats: [],
              fullPrice: 0,
              hasDiscount: false,
              isOK: true,
              priceWithDiscount: 0,
              coupon: ''
            }
          }

          // 1 stage: ICartTrack
          this.groups[b.beatmakerId].beats.push({
            ...b,
            notReady: true,
            licenses: this._getLicensesForBeat(b.id, licenses),
            get license(): ILicense {
              const selectedLicId = service.getLicenseForBeat(b.id)
              return this.licenses.filter(l => l.id === selectedLicId)[0]
            }
          })
        })
        this._refreshAllGroups()
      })
      .then(data => ({ ok: true }))
  }

  pay(groupId) {
    const group = this.groups.getGroups().filter(gr => gr.beatmakerId === groupId)[0]
    this.triggerEvent('pay', group)
  }

  getLicenseForBeat(beatId: number) {
    const beats = this.getBeats()
    const filteredBeats = beats.filter(beatItem => beatItem.beatId === beatId)
    if (!!filteredBeats.length) {
      return filteredBeats[0].licenseId
    }
    return null
  }

  getLicensesForBeats(): Promise<Array<ResponseLicenseType>> {
    const beatsIds = this._getBeatsIds()
    return this.cartApiService.getBeatsLicenses(beatsIds)
  }

  // удалить бит из корзины
  removeBeat(beatId: number) {
    this.cartStorageService.removeBeat(beatId)

    // удаляем полноценный бит с инфой из массива
    this.groups.removeBeat(beatId)

    this._refreshAllGroups()

    // подписчикам сообщаем, что у нас в группе что-то изменилось
    this.triggerGroupUpdated()
  }

  cleanOutTracks() {
    this.cartStorageService.updateShoppingCart([])
    this.triggerGroupUpdated()
  }

  // засетать для конкретной группы купон
  setCoupon(beatmakerId, coupon) {
    this.groups[beatmakerId].coupon = coupon
  }

  // проверить на серваке купон для конкретного битмаря, поэтому принимаем параметром id этого битмейкера
  checkCoupon(beatmakerId: number = null) {
    if (beatmakerId === null) {
      if (!this.groups.getGroups().length) {
        return
      } else {
        beatmakerId = this.groups.getGroups()[0].beatmakerId
      }
    }

    // достаём из нужной группы купон
    const coupon = this.groups[beatmakerId].coupon

    // готовим данные для отправки на сервак
    const beatsIds = this.groups[beatmakerId].beats.map(b => b.id)
    const allBeats: LocalStorageShoppingCart[] = this.getBeats()
    const beats = allBeats.filter(beat => beatsIds.indexOf(beat.beatId) > -1)

    return (
      this.cartApiService
        .checkCoupon(beatmakerId, coupon, beats)
        // Where is callback in then ? How it all works ?
        .then(this._processCheckResult.bind(this, beatmakerId))
    )
  }

  getGroup(beatmakerId: number): ICartGroup {
    return this.groups.getGroups().filter(g => g.beatmakerId === beatmakerId)[0]
  }

  getUrl(
    url: string,
    beatmakerId: number,
    paymentSystemType: number,
    email: string,
    phone: string,
    cId: string,
    wId = null,
    isEng: boolean,
    paymentSource: PaymentSources,
    clientData: { fullName: string | null; passportData: string | null }
  ) {
    const group = this.getGroup(beatmakerId)

    return this.cartApiService.getUrl(url, {
      eng: isEng,
      beatmakerId,
      paymentSystemType,
      email,
      phone,
      cId,
      coupon: group.coupon,
      wId,
      paymentSource,
      beats: this.getGroup(beatmakerId).beats.map(b => {
        return {
          beatId: b.id,
          licenseId: b.licenseId
        }
      }),
      clientData
    })
  }

  updateBeat(beatId: number, licenseId: string) {
    this.cartStorageService.updateBeat(beatId, licenseId)
    this._refreshAllGroups()
  }

  savePaymentIdForGroup(beatmakerId: number, paymentId: number, altPaymentId: string) {
    const beatmakersIdsAndPayments = this.cartStorageService.getPayments()

    const beatmakers = beatmakersIdsAndPayments.filter(el => el.beatmakerId === beatmakerId)
    if (!!beatmakers.length) {
      beatmakers[0].paymentId = paymentId
      beatmakers[0].altPaymentId = altPaymentId
      beatmakers[0].checkingCount = 0
    } else {
      beatmakersIdsAndPayments.push({ beatmakerId, paymentId, altPaymentId, checkingCount: 0 })
    }

    this.cartStorageService.savePayments(beatmakersIdsAndPayments)
  }

  isConfirmed(paymentId, altPaymentId) {
    return this.cartApiService.isConfirmed(paymentId, altPaymentId)
  }

  getClientData() {
    return this.cartApiService.getClientData()
  }

  // наша группа изменилась, поэтому нужно пересчитать цены, скидки и прочую чушь, поэтому
  // нужно отправить на сервак, чтобы всё синхронизировать
  private _refreshAllGroups() {
    this.groups.getGroups().forEach(group => {
      this.checkCoupon(group.beatmakerId)
    })
  }

  // В этом методе в ICartTrack добавляются свойства fullPrice... в group и также в group.beats
  private _processCheckResult(beatmakerId: number, groupResult: CheckResponseType) {
    const group = this.groups[beatmakerId]
    if (!!group) {
      group.fullPrice = groupResult.fullPrice
      group.hasDiscount = groupResult.hasDiscount
      group.isOK = groupResult.isOK
      group.priceWithDiscount = groupResult.priceWithDiscount
      group.hasRequiredPassportLicense = groupResult.hasRequiredPassportLicense
      group.cashbackAmount = groupResult.cashbackAmount

      group.beats.forEach(b => {
        // 2 stage: ICartTrack
        const newBeat = groupResult.beats.find(x => x.beatId === b.id)
        Object.assign(b, newBeat)
        b.notReady = true
      })
    }

    this.triggerGroupUpdated(beatmakerId)
  }

  private triggerGroupUpdated(beatmakerId: number | null = null) {
    this.triggerEvent('group-updated', { beatmakerId })
  }

  private _getBeatsIds() {
    const beats = this.getBeats()
    return beats.map(b => b.beatId)
  }

  private _getLicensesForBeat(beatId: number, licenses: Array<ResponseLicenseType>): Array<ILicense> {
    return licenses.filter(license => license.beatId === beatId)[0].licenses
  }

  private _removePaymentAndSave(beatmakerId) {
    const payments = this.cartStorageService.getPayments()
    const filteredPayments = payments.filter(pb => pb.beatmakerId !== beatmakerId)
    this.cartStorageService.savePayments(filteredPayments)
  }

  private _initGroup() {
    const that = this

    this.groups = {
      getBeatmakerIdAndBeatIndex(beatId): { beatmakerId; indexOfBeat } {
        let beatmakerId = -1
        let indexOfBeat = -1

        this.getGroups().forEach(group => {
          if (indexOfBeat > -1) {
            return
          }

          group.beats.forEach((b, index) => {
            if (b.id === beatId) {
              indexOfBeat = index
            }
          })

          if (indexOfBeat > -1) {
            beatmakerId = group.beatmakerId
          }
        })
        return { beatmakerId, indexOfBeat }
      },
      removeBeat(beatId) {
        const result = this.getBeatmakerIdAndBeatIndex(beatId)

        this[result.beatmakerId].beats.splice(result.indexOfBeat, 1)
        if (this[result.beatmakerId].beats.length === 0) {
          delete this[result.beatmakerId]
        }
        that._removePaymentAndSave(result.beatmakerId)
      },

      getProps() {
        return Object.keys(this).filter(key => typeof this[key] !== 'function')
      },

      getGroups(): Array<ICartGroup> {
        return this.getProps().map(key => this[key])
      }
    }
  }

  getPaymentsTypes(beatmakerId: number) {
    return this.cartApiService.getPaymentsTypes(beatmakerId)
  }
}
